import {
  Vehicle,
  MaintenanceRecord,
  MaintenanceScheduleGeneration,
  MaintenanceSchedule,
  UserMaintenanceSchedule,
  ScheduleFeedback,
} from "../types";
import { addMonths } from "date-fns";
import { supabase } from "../../../utils/supabase/client";

async function getCurrentUserId(): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("User not authenticated");
  }
  return user.id;
}

export const storage = {
  // Vehicles
  async getVehicles(): Promise<Vehicle[]> {
    const { data, error } = await supabase
      .from("vehicles")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching vehicles:", error);
      throw error;
    }

    return (data ?? []).map(mapVehicleFromDb);
  },

  async addVehicle(vehicle: Vehicle): Promise<void> {
    const userId = await getCurrentUserId();
    const { error } = await supabase.from("vehicles").insert({
      ...mapVehicleToDb(vehicle),
      user_id: userId,
    });

    if (error) {
      console.error("Error adding vehicle:", error);
      throw error;
    }
  },

  async updateVehicle(id: string, updates: Partial<Vehicle>): Promise<void> {
    const { error } = await supabase.from("vehicles").update(mapVehicleToDb(updates)).eq("id", id);

    if (error) {
      console.error("Error updating vehicle:", error);
      throw error;
    }
  },

  async deleteVehicle(id: string): Promise<void> {
    // Maintenance records will be deleted automatically via ON DELETE CASCADE
    const { error } = await supabase.from("vehicles").delete().eq("id", id);

    if (error) {
      console.error("Error deleting vehicle:", error);
      throw error;
    }
  },

  // Maintenance Records
  async getMaintenanceRecords(): Promise<MaintenanceRecord[]> {
    const { data, error } = await supabase
      .from("maintenance_records")
      .select("*")
      .order("date", { ascending: false });

    if (error) {
      console.error("Error fetching maintenance records:", error);
      throw error;
    }

    return (data ?? []).map(mapRecordFromDb);
  },

  async addMaintenanceRecord(record: MaintenanceRecord): Promise<void> {
    const userId = await getCurrentUserId();
    const { error } = await supabase.from("maintenance_records").insert({
      ...mapRecordToDb(record),
      user_id: userId,
    });

    if (error) {
      console.error("Error adding maintenance record:", error);
      throw error;
    }
  },

  async updateMaintenanceRecord(id: string, updates: Partial<MaintenanceRecord>): Promise<void> {
    const { error } = await supabase
      .from("maintenance_records")
      .update(mapRecordToDb(updates))
      .eq("id", id);

    if (error) {
      console.error("Error updating maintenance record:", error);
      throw error;
    }
  },

  async deleteMaintenanceRecord(id: string): Promise<void> {
    const { error } = await supabase.from("maintenance_records").delete().eq("id", id);

    if (error) {
      console.error("Error deleting maintenance record:", error);
      throw error;
    }
  },

  async deleteUserData(userId: string): Promise<void> {
    const { error: recordsError } = await supabase
      .from("maintenance_records")
      .delete()
      .eq("user_id", userId);

    if (recordsError) {
      console.error("Error deleting user maintenance records:", recordsError);
      throw recordsError;
    }

    const { error: vehiclesError } = await supabase.from("vehicles").delete().eq("user_id", userId);

    if (vehiclesError) {
      console.error("Error deleting user vehicles:", vehiclesError);
      throw vehiclesError;
    }
  },

  // Maintenance Schedule Generations

  async checkScheduleExists(make: string, model: string, year: number): Promise<boolean> {
    const { data, error } = await supabase
      .from("maintenance_schedule_generations")
      .select("id")
      .eq("make", make)
      .eq("model", model)
      .eq("year", year)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 = not found
      console.error("Error checking schedule existence:", error);
      throw error;
    }

    return data !== null;
  },

  async createGeneration(
    make: string,
    model: string,
    year: number
  ): Promise<MaintenanceScheduleGeneration> {
    const { data, error } = await supabase
      .from("maintenance_schedule_generations")
      .insert({
        make,
        model,
        year,
        status: "pending",
        human_verified: false,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating generation:", error);
      throw error;
    }

    return mapGenerationFromDb(data);
  },

  async updateGeneration(
    id: string,
    updates: Partial<MaintenanceScheduleGeneration>
  ): Promise<void> {
    const { error } = await supabase
      .from("maintenance_schedule_generations")
      .update(mapGenerationToDb(updates))
      .eq("id", id);

    if (error) {
      console.error("Error updating generation:", error);
      throw error;
    }
  },

  async getGeneration(id: string): Promise<MaintenanceScheduleGeneration> {
    const { data, error } = await supabase
      .from("maintenance_schedule_generations")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching generation:", error);
      throw error;
    }

    return mapGenerationFromDb(data);
  },

  async getGenerationByVehicle(
    make: string,
    model: string,
    year: number
  ): Promise<MaintenanceScheduleGeneration | null> {
    const { data, error } = await supabase
      .from("maintenance_schedule_generations")
      .select("*")
      .eq("make", make)
      .eq("model", model)
      .eq("year", year)
      .single();

    if (error && error.code !== "PGRST116") {
      console.error("Error fetching generation by vehicle:", error);
      throw error;
    }

    return data ? mapGenerationFromDb(data) : null;
  },

  // Maintenance Schedules

  async createMaintenanceSchedules(schedules: MaintenanceSchedule[]): Promise<void> {
    const { error } = await supabase
      .from("maintenance_schedules")
      .insert(schedules.map(mapScheduleToDb));

    if (error) {
      console.error("Error creating schedules:", error);
      throw error;
    }
  },

  async getMaintenanceSchedules(
    make: string,
    model: string,
    year: number
  ): Promise<MaintenanceSchedule[]> {
    const { data, error } = await supabase
      .from("maintenance_schedules")
      .select("*")
      .eq("make", make)
      .eq("model", model)
      .lte("year_start", year)
      .gte("year_end", year)
      .order("service_category");

    if (error) {
      console.error("Error fetching schedules:", error);
      throw error;
    }

    return (data ?? []).map(mapScheduleFromDb);
  },

  // Schedule Generation Trigger & Status

  async triggerScheduleGeneration(
    make: string,
    model: string,
    year: number
  ): Promise<MaintenanceScheduleGeneration> {
    const existing = await this.getGenerationByVehicle(make, model, year);
    if (existing) {
      return existing;
    }

    const generation = await this.createGeneration(make, model, year);

    // Fire-and-forget edge function invocation
    supabase.functions
      .invoke("generate-schedule", {
        body: { make, model, year, generationId: generation.id },
      })
      .catch((err) => {
        console.error("Error invoking generate-schedule:", err);
      });

    return generation;
  },

  async getGenerationStatus(
    make: string,
    model: string,
    year: number
  ): Promise<MaintenanceScheduleGeneration | null> {
    return this.getGenerationByVehicle(make, model, year);
  },

  async getSchedulesForVehicle(vehicleId: string): Promise<MaintenanceSchedule[]> {
    const userId = await getCurrentUserId();
    const { data: vehicleData, error: vehicleError } = await supabase
      .from("vehicles")
      .select("make, model, year")
      .eq("id", vehicleId)
      .eq("user_id", userId)
      .single();

    if (vehicleError) {
      console.error("Error fetching vehicle for schedules:", vehicleError);
      throw vehicleError;
    }

    return this.getMaintenanceSchedules(vehicleData.make, vehicleData.model, vehicleData.year);
  },

  async getUserSchedulesForVehicle(vehicleId: string): Promise<UserMaintenanceSchedule[]> {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from("user_maintenance_schedules")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error fetching user schedules:", error);
      throw error;
    }

    return (data ?? []).map(mapUserScheduleFromDb);
  },

  async initializeUserSchedules(
    vehicleId: string,
    schedules: MaintenanceSchedule[]
  ): Promise<void> {
    const userId = await getCurrentUserId();

    const { data: vehicleData, error: vehicleError } = await supabase
      .from("vehicles")
      .select("current_mileage")
      .eq("id", vehicleId)
      .eq("user_id", userId)
      .single();

    if (vehicleError) {
      console.error("Error fetching vehicle mileage:", vehicleError);
      throw vehicleError;
    }

    const currentMileage = vehicleData.current_mileage as number;
    const now = new Date();

    const rows = schedules.map((schedule) => ({
      user_id: userId,
      vehicle_id: vehicleId,
      schedule_id: schedule.id,
      next_due_date: schedule.intervalMonths
        ? addMonths(now, schedule.intervalMonths).toISOString().split("T")[0]
        : null,
      next_due_mileage: schedule.intervalMiles
        ? currentMileage + schedule.intervalMiles
        : null,
      is_dismissed: false,
    }));

    const { error } = await supabase.from("user_maintenance_schedules").insert(rows);

    if (error) {
      console.error("Error initializing user schedules:", error);
      throw error;
    }
  },

  async dismissScheduleItem(userScheduleId: string): Promise<void> {
    const { error } = await supabase
      .from("user_maintenance_schedules")
      .update({ is_dismissed: true })
      .eq("id", userScheduleId);

    if (error) {
      console.error("Error dismissing schedule item:", error);
      throw error;
    }
  },
};

// Map database snake_case to TypeScript camelCase for vehicles
function mapVehicleFromDb(row: Record<string, unknown>): Vehicle {
  return {
    id: row.id as string,
    make: row.make as string,
    model: row.model as string,
    year: row.year as number,
    vin: row.vin as string | undefined,
    licensePlate: row.license_plate as string | undefined,
    currentMileage: row.current_mileage as number,
    color: row.color as string | undefined,
    createdAt: row.created_at as string,
  };
}

function mapVehicleToDb(vehicle: Partial<Vehicle>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (vehicle.id !== undefined) result.id = vehicle.id;
  if (vehicle.make !== undefined) result.make = vehicle.make;
  if (vehicle.model !== undefined) result.model = vehicle.model;
  if (vehicle.year !== undefined) result.year = vehicle.year;
  if (vehicle.vin !== undefined) result.vin = vehicle.vin;
  if (vehicle.licensePlate !== undefined) result.license_plate = vehicle.licensePlate;
  if (vehicle.currentMileage !== undefined) result.current_mileage = vehicle.currentMileage;
  if (vehicle.color !== undefined) result.color = vehicle.color;
  if (vehicle.createdAt !== undefined) result.created_at = vehicle.createdAt;
  return result;
}

// Map database snake_case to TypeScript camelCase for maintenance records
function mapRecordFromDb(row: Record<string, unknown>): MaintenanceRecord {
  return {
    id: row.id as string,
    vehicleId: row.vehicle_id as string,
    type: row.type as string,
    date: row.date as string,
    mileage: row.mileage as number,
    cost: Number(row.cost),
    notes: row.notes as string | undefined,
    serviceProvider: row.service_provider as string | undefined,
    createdAt: row.created_at as string,
  };
}

function mapRecordToDb(record: Partial<MaintenanceRecord>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (record.id !== undefined) result.id = record.id;
  if (record.vehicleId !== undefined) result.vehicle_id = record.vehicleId;
  if (record.type !== undefined) result.type = record.type;
  if (record.date !== undefined) result.date = record.date;
  if (record.mileage !== undefined) result.mileage = record.mileage;
  if (record.cost !== undefined) result.cost = record.cost;
  if (record.notes !== undefined) result.notes = record.notes;
  if (record.serviceProvider !== undefined) result.service_provider = record.serviceProvider;
  if (record.createdAt !== undefined) result.created_at = record.createdAt;
  return result;
}

// Map database snake_case to TypeScript camelCase for maintenance schedule generations
function mapGenerationFromDb(row: Record<string, unknown>): MaintenanceScheduleGeneration {
  return {
    id: row.id as string,
    make: row.make as string,
    model: row.model as string,
    year: row.year as number,
    status: row.status as "pending" | "completed" | "failed" | "needs_review",
    confidenceScore: row.confidence_score as number | undefined,
    sourcesUsed: row.sources_used as string[] | undefined,
    aiRawOutput: row.ai_raw_output,
    errorMessage: row.error_message as string | undefined,
    humanVerified: row.human_verified as boolean,
    humanVerifiedBy: row.human_verified_by as string | undefined,
    humanVerifiedAt: row.human_verified_at as string | undefined,
    createdAt: row.created_at as string,
    completedAt: row.completed_at as string | undefined,
  };
}

function mapGenerationToDb(gen: Partial<MaintenanceScheduleGeneration>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (gen.id !== undefined) result.id = gen.id;
  if (gen.make !== undefined) result.make = gen.make;
  if (gen.model !== undefined) result.model = gen.model;
  if (gen.year !== undefined) result.year = gen.year;
  if (gen.status !== undefined) result.status = gen.status;
  if (gen.confidenceScore !== undefined) result.confidence_score = gen.confidenceScore;
  if (gen.sourcesUsed !== undefined) result.sources_used = gen.sourcesUsed;
  if (gen.aiRawOutput !== undefined) result.ai_raw_output = gen.aiRawOutput;
  if (gen.errorMessage !== undefined) result.error_message = gen.errorMessage;
  if (gen.humanVerified !== undefined) result.human_verified = gen.humanVerified;
  if (gen.humanVerifiedBy !== undefined) result.human_verified_by = gen.humanVerifiedBy;
  if (gen.humanVerifiedAt !== undefined) result.human_verified_at = gen.humanVerifiedAt;
  if (gen.createdAt !== undefined) result.created_at = gen.createdAt;
  if (gen.completedAt !== undefined) result.completed_at = gen.completedAt;
  return result;
}

// Map database snake_case to TypeScript camelCase for maintenance schedules
function mapScheduleFromDb(row: Record<string, unknown>): MaintenanceSchedule {
  return {
    id: row.id as string,
    make: row.make as string,
    model: row.model as string,
    yearStart: row.year_start as number,
    yearEnd: row.year_end as number,
    serviceName: row.service_name as string,
    serviceCategory: row.service_category as
      | "Engine Oil"
      | "Transmission"
      | "Brakes"
      | "Tires"
      | "Filters"
      | "Fluids"
      | "Belts & Hoses"
      | "Battery"
      | "Inspection"
      | "Other",
    intervalMonths: row.interval_months as number | undefined,
    intervalMiles: row.interval_miles as number | undefined,
    description: row.description as string | undefined,
    isSevereDriving: row.is_severe_driving as boolean,
    partsNeeded: row.parts_needed as string[] | undefined,
    estimatedCostMin: row.estimated_cost_min as number | undefined,
    estimatedCostMax: row.estimated_cost_max as number | undefined,
    priority: row.priority as "critical" | "recommended" | "optional" | undefined,
    generationId: row.generation_id as string,
    createdAt: row.created_at as string,
  };
}

function mapScheduleToDb(schedule: Partial<MaintenanceSchedule>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (schedule.id !== undefined) result.id = schedule.id;
  if (schedule.make !== undefined) result.make = schedule.make;
  if (schedule.model !== undefined) result.model = schedule.model;
  if (schedule.yearStart !== undefined) result.year_start = schedule.yearStart;
  if (schedule.yearEnd !== undefined) result.year_end = schedule.yearEnd;
  if (schedule.serviceName !== undefined) result.service_name = schedule.serviceName;
  if (schedule.serviceCategory !== undefined) result.service_category = schedule.serviceCategory;
  if (schedule.intervalMonths !== undefined) result.interval_months = schedule.intervalMonths;
  if (schedule.intervalMiles !== undefined) result.interval_miles = schedule.intervalMiles;
  if (schedule.description !== undefined) result.description = schedule.description;
  if (schedule.isSevereDriving !== undefined) result.is_severe_driving = schedule.isSevereDriving;
  if (schedule.partsNeeded !== undefined) result.parts_needed = schedule.partsNeeded;
  if (schedule.estimatedCostMin !== undefined)
    result.estimated_cost_min = schedule.estimatedCostMin;
  if (schedule.estimatedCostMax !== undefined)
    result.estimated_cost_max = schedule.estimatedCostMax;
  if (schedule.priority !== undefined) result.priority = schedule.priority;
  if (schedule.generationId !== undefined) result.generation_id = schedule.generationId;
  if (schedule.createdAt !== undefined) result.created_at = schedule.createdAt;
  return result;
}

// Map database snake_case to TypeScript camelCase for user maintenance schedules
function mapUserScheduleFromDb(row: Record<string, unknown>): UserMaintenanceSchedule {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    vehicleId: row.vehicle_id as string,
    scheduleId: row.schedule_id as string,
    lastCompletedAt: row.last_completed_at as string | undefined,
    lastCompletedMileage: row.last_completed_mileage as number | undefined,
    nextDueDate: row.next_due_date as string | undefined,
    nextDueMileage: row.next_due_mileage as number | undefined,
    isDismissed: row.is_dismissed as boolean,
    userNotes: row.user_notes as string | undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

